import React, {useState} from 'react';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import {useIntl} from 'react-intl';

import BandMultiSelect from './BandMultiSelect';

export const BAND_OPTIONS = ['featured', 'cooling', 'candidate', 'none'];

/** Valores de los selects de bandera: '' = Todos (no filtra). */
const FLAG_ALL = '';

export const EMPTY_DRAFT = {
    eventId: '',
    name: '',
    band: [],
    demandBannerEnabled: FLAG_ALL,
    autoRankingEnabled: FLAG_ALL,
};

const EMPTY_TEXT = {eventId: '', name: ''};

/**
 * Convierte el borrador del formulario en los filtros que se mandan al
 * backend. Solo incluye los campos con valor: un id inválido, un nombre vacío
 * o una bandera en "Todos" no filtran.
 */
export function draftToFilters(draft) {
    const filters = {};
    const id = parseInt(draft.eventId, 10);
    if (id > 0) filters.eventId = id;
    const name = (draft.name || '').trim();
    if (name) filters.name = name;
    if (draft.band && draft.band.length > 0) filters.band = [...draft.band];
    if (draft.demandBannerEnabled !== FLAG_ALL) {
        filters.demandBannerEnabled = draft.demandBannerEnabled === 'true';
    }
    if (draft.autoRankingEnabled !== FLAG_ALL) {
        filters.autoRankingEnabled = draft.autoRankingEnabled === 'true';
    }
    return filters;
}

export function hasDraftValues(draft) {
    return Object.keys(draftToFilters(draft)).length > 0;
}

/**
 * Barra de filtros de una tabla de promovidos (Manuales o Computados). Cada
 * tabla tiene la suya y no comparten estado.
 *
 * - Nivel y banderas se aplican al elegir una opción.
 * - Id y nombre (los que se escriben) solo se aplican con "Aplicar" (o Enter).
 *   Mientras tanto, elegir una opción usa el último id/nombre APLICADO, no lo
 *   que esté a medio escribir.
 */
function PromotedFilterBar({onApply, onClear, testIdPrefix, label, active}) {
    const intl = useIntl();
    const [draft, setDraft] = useState(EMPTY_DRAFT);
    const [appliedText, setAppliedText] = useState(EMPTY_TEXT);
    const t = (id) => intl.formatMessage({id});
    const testId = (suffix) => `${testIdPrefix}${suffix}`;
    const setText = (key) => (value) =>
        setDraft((previous) => ({...previous, [key]: value}));

    function applyOption(key, value) {
        const next = {...draft, [key]: value};
        setDraft(next);
        onApply(draftToFilters({...next, ...appliedText}));
    }

    function handleSubmit(e) {
        e.preventDefault();
        setAppliedText({eventId: draft.eventId, name: draft.name});
        onApply(draftToFilters(draft));
    }

    function handleClear() {
        setDraft(EMPTY_DRAFT);
        setAppliedText(EMPTY_TEXT);
        onClear();
    }

    const flagSelect = (key, labelId) => (
        <Form.Group className='mr-2 mb-2' controlId={testId(`-${key}`)}>
            <Form.Label srOnly>{t(labelId)}</Form.Label>
            <Form.Control
                as='select'
                custom
                size='sm'
                value={draft[key]}
                onChange={(e) => applyOption(key, e.target.value)}
                data-test={testId(
                    key === 'demandBannerEnabled'
                        ? 'FilterDemandBanner'
                        : 'FilterAutoRanking',
                )}
            >
                <option value={FLAG_ALL}>
                    {`${t(labelId)}: ${t('RELEVANCE.PROMOTED_FILTER_ALL')}`}
                </option>
                <option value='true'>
                    {`${t(labelId)}: ${t(
                        'RELEVANCE.PROMOTED_AUTO_RANKING_ON',
                    )}`}
                </option>
                <option value='false'>
                    {`${t(labelId)}: ${t(
                        'RELEVANCE.PROMOTED_AUTO_RANKING_OFF',
                    )}`}
                </option>
            </Form.Control>
        </Form.Group>
    );

    return (
        <Form
            inline
            role='search'
            aria-label={label}
            onSubmit={handleSubmit}
            className='mb-2'
            data-test={testId('FilterBar')}
        >
            <Form.Group className='mr-2 mb-2' controlId={testId('-eventId')}>
                <Form.Label srOnly>
                    {t('RELEVANCE.PROMOTED_FILTER_EVENT_ID')}
                </Form.Label>
                <Form.Control
                    size='sm'
                    type='number'
                    min='1'
                    style={{width: 120}}
                    value={draft.eventId}
                    placeholder={t('RELEVANCE.PROMOTED_FILTER_EVENT_ID')}
                    onChange={(e) => setText('eventId')(e.target.value)}
                    data-test={testId('FilterEventId')}
                />
            </Form.Group>
            <Form.Group className='mr-2 mb-2' controlId={testId('-name')}>
                <Form.Label srOnly>
                    {t('RELEVANCE.PROMOTED_FILTER_NAME')}
                </Form.Label>
                <Form.Control
                    size='sm'
                    type='text'
                    maxLength={100}
                    style={{width: 200}}
                    value={draft.name}
                    placeholder={t('RELEVANCE.PROMOTED_FILTER_NAME')}
                    onChange={(e) => setText('name')(e.target.value)}
                    data-test={testId('FilterName')}
                />
            </Form.Group>
            <BandMultiSelect
                id={testId('-band')}
                options={BAND_OPTIONS}
                value={draft.band}
                onChange={(band) => applyOption('band', band)}
                placeholder={t('RELEVANCE.PROMOTED_FILTER_BAND')}
                testId={testId('FilterBand')}
            />
            {flagSelect(
                'demandBannerEnabled',
                'RELEVANCE.PROMOTED_FILTER_DEMAND_BANNER',
            )}
            {flagSelect(
                'autoRankingEnabled',
                'RELEVANCE.PROMOTED_FILTER_AUTO_RANKING',
            )}
            <Button
                type='submit'
                variant='primary'
                size='sm'
                className='mb-2'
                data-test={testId('FilterApply')}
            >
                {t('RELEVANCE.PROMOTED_FILTER_APPLY')}
            </Button>
            {(active || hasDraftValues(draft)) && (
                <Button
                    variant='link'
                    size='sm'
                    className='mb-2'
                    onClick={handleClear}
                    data-test={testId('FilterClear')}
                >
                    {t('RELEVANCE.PROMOTED_FILTER_CLEAR')}
                </Button>
            )}
        </Form>
    );
}

// memo: la tabla se vuelve a pintar en cada carga (loading); la barra solo
// cuando cambian sus props, que el padre mantiene estables (rerender-memo).
export default React.memo(PromotedFilterBar);
